import React, { FC, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { pagePathUtils, SignOut } from "~/utils";

import {
  MenuType,
  useBoolean,
  useMenus,
  useProvidersCurrentEmployee,
} from "~/hooks";

import { FloatingChatBox } from "~/components/organisms";

import { SidebarCurrentMenu } from "./SidebarMenu/SidebarCurrentMenu";
import { SidebarMenu } from "./SidebarMenu/SidebarMenu";

import {
  AnalyticsPlanType,
  ApiMembersUnreadNotificationCountsType,
  BreadCrumbItemType,
  ManagerOrOwnerRoles,
} from "~/domains";

import { AIAvatar, Avatar, Link, ModalWrapper } from "~/ui";

type PropsType = {
  className?: string;
  unreadNotification?: ApiMembersUnreadNotificationCountsType;
  currentMenu: MenuType;
  breadCrumbItems: BreadCrumbItemType[];
  setCurrentMenu: (menu: MenuType) => void;
};

export const Sidebar: FC<PropsType> = ({
  className = "",
  unreadNotification,
  currentMenu,
  breadCrumbItems,
  setCurrentMenu,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const isManagerPage = pagePathUtils.isManagerPage();
  const { menus } = useMenus();
  const {
    isChecked: isAvatarPopupOpen,
    setTrue: setAvatarPopupOpen,
    setFalse: setAvatarPopupClose,
  } = useBoolean(false);
  const {
    isChecked: isChatOpen,
    setTrue: openChat,
    setFalse: closeChat,
  } = useBoolean(false);

  const isManagerOrOwner = ManagerOrOwnerRoles.some(
    (role) => role.id === currentEmployee?.employeeRole.id,
  );
  const isAnalyticsPlan =
    currentEmployee?.company.contractPlan.id === AnalyticsPlanType.id;
  const navigate = useNavigate();

  const handleOpenChat = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    openChat();
  };

  const onClickSignOut = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const result = confirm("本当にログアウトしますか？");
    if (!result) return;

    await SignOut({
      isAdmin: false,
      onSuccess: () => {
        toast("ログアウトしました");
        navigate("/sign_in");
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  };

  return (
    <div
      className={`sticky z-sidebar top-header-offset h-full hidden miniTablet:flex min-w-[250px] items-start ${className}`}
    >
      <aside className="flex flex-col h-full">
        {menus.map((menu) => (
          <SidebarMenu
            key={menu.type}
            menuType={menu.type}
            href={menu.href}
            unreadNotification={unreadNotification}
            selectMenu={setCurrentMenu}
            currentMenuType={currentMenu}
          />
        ))}
        {currentEmployee && (
          <div className="relative mt-auto pt-4 pb-4 flex flex-col items-center gap-3 border-t border-secondary-200 mx-2">
            <button
              onClick={handleOpenChat}
              type="button"
              className="p-1.5 rounded-xl hover:bg-secondary-100"
            >
              <AIAvatar type="neutralExpression" size="sm" />
            </button>
            <button
              onClick={setAvatarPopupOpen}
              type="button"
              className="p-1.5 rounded-xl hover:bg-secondary-100"
            >
              <Avatar src={currentEmployee.avatarUrl} size="sm" />
            </button>
            {isChatOpen && <FloatingChatBox onClose={closeChat} />}
            {isAvatarPopupOpen && (
              <>
                <ModalWrapper isWhite onClose={setAvatarPopupClose} />
                <div className="absolute left-full bottom-2 ml-3 w-max z-modal shadow-lg p-2 text-left rounded-xl bg-white border border-secondary-200 text-secondary-600">
                  <Link
                    to={isManagerPage ? "/managers/profile" : "/profile"}
                    className="block hover:bg-secondary-100 py-2 px-3 rounded-lg text-sm"
                  >
                    マイページ
                  </Link>
                  <Link
                    to="/"
                    color="primary"
                    className="block hover:bg-secondary-100 py-2 px-3 rounded-lg text-sm"
                  >
                    メンバーページ
                  </Link>
                  {isManagerOrOwner && (
                    <>
                      <Link
                        to="/managers"
                        color="primary"
                        className="block hover:bg-secondary-100 py-2 px-3 rounded-lg text-sm"
                      >
                        管理者ページ
                      </Link>
                      {isAnalyticsPlan && (
                        <Link
                          to="/analytics_plans"
                          color="primary"
                          className="block hover:bg-secondary-100 py-2 px-3 rounded-lg text-sm"
                        >
                          アナリティクスページ
                        </Link>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={onClickSignOut}
                    className="block w-full text-left hover:bg-secondary-100 py-2 px-3 rounded-lg text-sm"
                  >
                    ログアウト
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </aside>
      <div className="w-full bg-primary-200 hidden-scrollbar miniTablet-screen-height overflow-auto py-4">
        <SidebarCurrentMenu
          menu={currentMenu}
          breadCrumbItems={breadCrumbItems}
          unreadNotification={unreadNotification}
        />
      </div>
    </div>
  );
};
